import React, { useEffect, useState } from "react";
import { PiGift } from "react-icons/pi";
import {
  getLoyaltySettings,
  getMyLoyalty,
  getMyLoyaltyTransactions,
} from "../../api/loyaltyApi";
import {
  buildLoyaltyExplanation,
  formatLoyaltyDate,
  formatKopecks,
  LOYALTY_OPERATION_COMMENTS,
  LOYALTY_OPERATION_LABELS,
} from "../../utils/loyalty";
import styles from "./LoyaltySection.module.css";
import { useLoyaltySettings } from "../../context/LoyaltySettingsContext";

const PAGE_SIZE = 10;

const LoyaltySection = () => {
  const { settings: sharedSettings } = useLoyaltySettings();
  const [summary, setSummary] = useState(null);
  const [settings, setSettings] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();

    const loadSummary = async () => {
      setSummaryLoading(true);
      setError("");
      try {
        const [summaryResult, settingsResult] = await Promise.allSettled([
          getMyLoyalty({ signal: controller.signal }),
          getLoyaltySettings({ signal: controller.signal }),
        ]);

        if (summaryResult.status === "fulfilled") {
          setSummary(summaryResult.value);
          if (summaryResult.value.settings) {
            setSettings(summaryResult.value.settings);
          }
        } else {
          throw summaryResult.reason;
        }

        if (settingsResult.status === "fulfilled") {
          setSettings(settingsResult.value);
        }
      } catch (loadError) {
        if (loadError?.code !== "ERR_CANCELED") {
          setError(
            loadError?.response?.data?.message ||
              "Не вдалося завантажити баланс бонусів.",
          );
        }
      } finally {
        if (!controller.signal.aborted) setSummaryLoading(false);
      }
    };

    loadSummary();
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const loadTransactions = async () => {
      setHistoryLoading(true);
      try {
        const result = await getMyLoyaltyTransactions(
          { page, limit: PAGE_SIZE },
          { signal: controller.signal },
        );
        setTransactions(result.items);
        setTotalPages(result.totalPages);
        if (result.page !== page) setPage(result.page);
      } catch (loadError) {
        if (loadError?.code !== "ERR_CANCELED") {
          setError(
            loadError?.response?.data?.message ||
              "Не вдалося завантажити історію бонусів.",
          );
        }
      } finally {
        if (!controller.signal.aborted) setHistoryLoading(false);
      }
    };

    loadTransactions();
    return () => controller.abort();
  }, [page]);

  const activeBonusKopecks = summary?.availableBonusKopecks ?? 0;

  return (
    <div className={styles.root}>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionIcon}>
          <PiGift />
        </div>
        <div>
          <span>MyZooShop Club</span>
          <h1>Бонусна програма</h1>
          <p>
            Переглядайте активний баланс, очікувані нарахування, строк дії та
            повну історію операцій.
          </p>
        </div>
      </div>

      {error && (
        <div className={styles.error} role="alert">
          {error}
        </div>
      )}

      {summaryLoading ? (
        <div className={styles.loading}>Завантажуємо баланс бонусів…</div>
      ) : (
        <>
          <section className={styles.balanceHero}>
            <div>
              <span className={styles.eyebrow}>Доступний баланс</span>
              <strong>{formatKopecks(activeBonusKopecks)}</strong>
              <p>Ваш бонусний баланс у гривнях</p>
            </div>
            <PiGift aria-hidden="true" />
          </section>

          <div className={styles.statsGrid}>
            <article>
              <span>Очікують активації</span>
              <strong>{formatKopecks(summary?.pendingBonusKopecks ?? 0)}</strong>
            </article>
            <article>
              <span>Використано</span>
              <strong>{formatKopecks(summary?.redeemedBonusKopecks ?? 0)}</strong>
            </article>
            <article>
              <span>Прострочено</span>
              <strong>{formatKopecks(summary?.expiredBonusKopecks ?? 0)}</strong>
            </article>
            <article>
              <span>Діють до</span>
              <strong className={styles.expiryValue}>
                {formatLoyaltyDate(summary?.expiresAt, { includeTime: false })}
              </strong>
            </article>
          </div>
        </>
      )}

      {settings && (
        <section className={styles.rulesCard}>
          <h2>Актуальні правила</h2>
          <p>{buildLoyaltyExplanation(sharedSettings || settings)}</p>
          {settings.enabled && (
            <ul>
              <li>
                Максимальне списання: {settings.maxRedeemPercent}% вартості
                товарів.
              </li>
              <li>Строк дії: {settings.expirationMonths} міс.</li>
            </ul>
          )}
        </section>
      )}

      <section className={styles.historyCard}>
        <div className={styles.historyHeader}>
          <div>
            <span>Рух бонусів</span>
            <h2>Історія нарахування та списання бонусів</h2>
          </div>
          <span className={styles.pageBadge}>
            Сторінка {page} з {totalPages}
          </span>
        </div>

        {historyLoading ? (
          <div className={styles.loading}>Завантажуємо історію…</div>
        ) : transactions.length === 0 ? (
          <div className={styles.empty}>Операцій з бонусами ще немає.</div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Дата та час операції</th>
                  <th>Операція</th>
                  <th>Бонуси</th>
                  <th>Замовлення</th>
                  <th>Коментар</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((transaction) => {
                  const positive = transaction.amountKopecks > 0;
                  return (
                    <tr key={transaction.id}>
                      <td data-label="Дата та час операції">
                        {formatLoyaltyDate(transaction.createdAt)}
                      </td>
                      <td data-label="Операція">
                        {LOYALTY_OPERATION_LABELS[transaction.type] ||
                          transaction.type ||
                          "Операція"}
                      </td>
                      <td
                        data-label="Сума"
                        className={positive ? styles.credit : styles.debit}
                      >
                        {formatKopecks(transaction.amountKopecks, { signed: true })}
                      </td>
                      <td data-label="Замовлення">
                        {transaction.orderNumber
                          ? `№${transaction.orderNumber}`
                          : "—"}
                      </td>
                      <td data-label="Коментар">
                        {LOYALTY_OPERATION_COMMENTS[transaction.type] ||
                          transaction.reason ||
                          "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className={styles.pagination}>
            <button
              type="button"
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={page <= 1 || historyLoading}
            >
              Назад
            </button>
            <span>
              {page} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() =>
                setPage((current) => Math.min(totalPages, current + 1))
              }
              disabled={page >= totalPages || historyLoading}
            >
              Далі
            </button>
          </div>
        )}
      </section>
    </div>
  );
};

export default LoyaltySection;

